<template>
  <JdsSidePanel
    @close="$emit('close')"
    background-class="fe-bg-black"
    :remove-horizontal-class="true"
  >
    <template #body>
      <div class="fe-fixed fe-top-0 fe-right-0 fe-px-xs fe-py-s">
        <JdsButton @click="handleClose" :icon-only="true" variant="tertiary">
          <template #icon>
            <JdsIcon name="CloseIcon" size="m" color="fe-text-primary-60" />
          </template>
        </JdsButton>
      </div>

      <div class="fe-h-full">
        <div
          class="fe-w-full fe-bg-black fe-overflow-y-scroll fe-no-scrollbar"
          @touchstart="touchStart"
          @touchmove="touchMove"
          @touchend="touchEnd"
        >
          <!-- Here image container height is calc(100vh (screen height) - 164px (padding-top + close button container height) - 56px (dots container height))  -->
          <LazyImage
            class-name="fe-w-full fe-pt-xxl"
            img-class-name="fe-object-contain fe-block"
            :src="currentImage.midResUrl"
            placeholder="none"
            :style="{ height: imageContainerHeight }"
          />
        </div>

        <div
          v-if="currentVisibleReview"
          ref="reviewContainer"
          class="fe-max-h-[80vh] fe-absolute fe-bottom-0 fe-left-0 fe-right-0 fe-overflow-y-auto fe-no-scrollbar fe-rounded-t-xl fe-bg-white fe-px-m fe-py-base"
        >
          <ReviewListItem
            :review="currentVisibleReview"
            :hide-read-more-less-button="false"
            :is-sm-content-modal="true"
            displayContext="content_modal"
            @click="handleThumbnailClick"
          />
        </div>
      </div>
    </template>
  </JdsSidePanel>
</template>

<script>
  import JdsSidePanel from "@/components/DesignSystem/JdsSidePanel/JdsSidePanel.vue"
  import JdsButton from "@/components/DesignSystem/JdsButton/JdsButton.vue"
  import JdsIcon from "@/components/DesignSystem/JdsIcon/JdsIcon.vue"
  import LazyImage from "@/components/DesignSystem/LazyImage/LazyImage.vue"
  import ReviewListItem from "@/components/ReviewAndRating/ReviewList/ReviewListItem.vue"
  import { modalMixin } from "@/mixins/modalMixin"
  import GalleryImageModal from "@/components/ReviewAndRating/Gallery/GalleryImageModal.vue"

  export default {
    name: "MobileContentModal",
    components: { JdsSidePanel, JdsButton, JdsIcon, LazyImage, ReviewListItem },
    data() {
      return {
        currentIndex: this.initialVisibleImageIndex,
        touchStartX: 0,
        touchEndX: 0,
        showPrevReview: false,
        imageContainerHeight: "100%",
      }
    },
    mixins: [modalMixin],
    props: {
      initialVisibleImageIndex: { type: Number, default: 0 },
      shouldOpenImageModal: { type: Boolean, default: false },
      currentVisibleReview: { type: Object, default: () => ({}) },
      showCurrentReviewOnly: { type: Boolean, default: false },
      hideNextNav: { type: Boolean, default: false },
      hidePreviousNav: { type: Boolean, default: false },
    },
    watch: {
      currentVisibleReview() {
        // This is needed because when user view the next review, and if we don't change currentIndex, it will break because in next review there might not be any image on that index number
        if (!this.showPrevReview) {
          this.currentIndex = 0
        } else {
          // If user is moving to previous review, then we should show the last image of previous review
          this.currentIndex = this.images?.length - 1 || 0
          this.showPrevReview = false
        }
      },
      initialVisibleImageIndex(newVal) {
        this.currentIndex = newVal
      },
    },
    computed: {
      images() {
        return this.currentVisibleReview?.images
      },
      currentImage() {
        return this.images[this.currentIndex]
      },
    },
    mounted() {
      this.updateImageContainerHeight()
    },
    methods: {
      updateImageContainerHeight() {
        // Get the height of review container, then subtract it from total screen height
        // That will become height of image container
        // And since this method is not reactive, so it will calculate the value of review container only when review content text is hidden using read more button
        // After that even if user clicks on read more button (which will increase the height of review container), it will not update the height of image container
        this.$nextTick(() => {
          const reviewContainerHeight =
            this.$refs.reviewContainer?.clientHeight || 0

          this.imageContainerHeight = `calc(100vh - ${reviewContainerHeight}px)`
        })
      },
      setCurrentImage(index) {
        this.currentIndex = index
      },
      touchStart(e) {
        this.touchStartX = e.changedTouches[0].screenX
      },
      touchMove(e) {
        this.touchEndX = e.changedTouches[0].screenX
      },
      touchEnd() {
        if (this.touchStartX - this.touchEndX > 50) {
          // Swipe left
          this.nextSlide()
        } else if (this.touchEndX - this.touchStartX > 50) {
          // Swipe right
          this.prevSlide()
        }
      },
      nextSlide() {
        if (this.currentIndex < this.images.length - 1) {
          this.currentIndex++
        } else if (!this.showCurrentReviewOnly) {
          this.$emit("nextReview")
        } else {
          this.currentIndex = 0
        }
      },
      prevSlide() {
        if (this.currentIndex > 0) {
          this.currentIndex--
        } else if (!this.showCurrentReviewOnly) {
          this.showPrevReview = true
          this.$emit("prevReview")
        } else {
          this.currentIndex = this.images.length - 1
        }
      },
      handleClose() {
        if (this.shouldOpenImageModal) {
          this.openModal(GalleryImageModal)
        } else {
          this.closeModal()
        }
      },
      handleThumbnailClick(reviewData) {
        const findCurrentReviewIndex =
          reviewData?.currentReview?.images?.findIndex(
            (image) => image.id === reviewData?.currentImageId,
          )

        this.currentIndex =
          findCurrentReviewIndex === -1 ? 0 : findCurrentReviewIndex
      },
    },
  }
</script>
